// @givmo/app-charity — `/settings`: the gear's ONE composed settings page (D-CPO-33; approved
// for now by D-CPO-34; the OM-9 build). The company console's twin.
//
// The address is `SETTINGS_ROUTE` (`/settings`) since Org Machinery OM-17's cut-over (RULED
// 2026-09-07) — ONE move on both hosts, with `/preferences` (the OM-9 build's address) kept as a
// replace-redirect. The catalog-removal request page that held `/settings` (CPA-WEB7) answers
// `/removal` now, titled "Remove from Givmo"; this page's Organization section carries the card that
// is the DOOR into it. (This feature folder keeps its `preferences` name: the never-overwrite and
// build-law rails scan it by that path.)
//
// Composed for the person, in this order on EVERY door — the gear and the sidebar's LAST row alike
// (the package's `OrgSettingsPanel`; Org Machinery OM-17 design 2):
//   1. "<Module> settings" — the module the page was opened from: the definitions the server
//      serves for it (seam A) and the panels it declares (seam B,
//      `consoleDeclarations().moduleSettings` — Board is the first declarer; the shell's own
//      approver card went with that setting, EP-WEB36),
//      each gated on the ONE permission string its write requires; from an Admin Console page
//      there is no module;
//   2. "Your settings" — the person's "Sign-in & security" card first (AS-WEB11; it renders nothing
//      until the backend serves passkeys here), then, where Board is compiled in, the "Calendar"
//      card (CBG-CAL1; since CBG-UX14's Q3 the person's own calendar for any Givmo app, its row
//      naming Board's use; it renders nothing until the backend serves the calendar) for a holder of
//      `board_meeting:write`, but not on Board's own door, where Board's "Calendar" card holds the
//      same connection (D-CBG-56), and, wherever Board isn't on for the organization, for anyone:
//      revoking access never waits on Board, so there it offers the owner of a calendar its
//      Disconnect alone (core lists nobody else's), then the shipped Landing + Notifications cards;
//   3. "Organization", at the foot — this class's declared cards (`organizationSettings`) and the
//      *Deactivate organization* card last, rendered ONLY when the console's ONE standing read
//      carries the `deactivate_org` capability (the string, never a tier — the build law). A
//      not-reported standing renders "Your settings" alone. On Board's own door "Connected accounts"
//      is left out (CBG-UX14's Q2): for a holder of `board_meeting:write` there, Board's "Calendar"
//      card above holds the calendars the board's meetings go out from.
//
// What this host supplies: the MODULE the page was opened from — the frame the shell's sidebar
// wears on this page (`components/console-frame-context.ts`; the sidebar reads the origin off the
// page's address, `console/settings-origin.ts`, and falls to the cold-load rule where it gives the
// person no place to stand — Org Machinery OM-28 and its review fold), the landing OPTIONS (the openable squares, never the Admin
// Console — R1), what "Default" resolves to right now, the caller's effective set, capabilities and
// per-module standings off `GET /org/{ref}/me`, its declarations, the class copy (through the
// console config's vocab), the in-app navigate, and the door: once the act lands, the portal's ONE
// sign-out (`useSignOut`) clears the session and lands the person on the plain `/login-home` door
// — no notice (RULED 2026-09-05, "plain").
//
// ⚠ NO SECOND STANDING-READ REFRESH HERE, unlike the company console: this host's standing read IS
// the package's own `self` query, which the preferences write already invalidates.
//
// DATA CONTRACT: `page-settings` is the route's root (OM-17 design 2: `page-preferences` RETIRED, not re-used; the e2e helper re-pins at the consumer cut);
// inside it the package's `org-settings-*`, `org-preferences-*` and `org-deactivation-*` families
// (fixed, not prefixed).

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersGetMeOptions } from '@givmo/api-client/query';
import {
  BOARD_SLUG,
  YourCalendarCard,
  useConnectedAccountsShown,
  useHasBoardRuntime,
} from '@givmo/board';
import { testIdProp } from '@givmo/design-system';
import {
  OrgSettingsPanel,
  isMountedInThisBuild,
  openableSquares,
  verdictFor,
  type ModuleSettings,
  type OrgSettingsModule,
} from '@givmo/org-console';
import { useSignOut } from '../../auth/use-sign-out';
import { useConsoleFrame } from '../../components/console-frame-context';
import { PageShell } from '../../components/page-shell';
import { useCharityConsole } from '../../console/charity-console-context';
import { CONNECTED_ACCOUNTS_CARD, consoleDeclarations } from '../../console/console-nav';
import { charityConsoleTable } from '../../console/module-table';
import { useConsoleSquares, useModuleNavigate } from '../../console/use-console-squares';
import { isBoardUiEnabled } from '../../lib/env';
import { useUnsavedChanges } from '../../lib/unsaved-changes';
import { useActiveOrg } from '../../org/active-org-context';
import { useLandingDestination } from '../../router/console-landing';
import { useOrgConsoleConfig } from '../org-console/use-org-console-config';
import { CharitySignInSecurityCard } from '../sign-in-security/charity-sign-in-security-card';

/**
 * The module the page was opened from — the MOUNTED module whose frame the shell's sidebar wears on
 * this page (`useConsoleFrame`), with the panels it declares — or `null` where no module frames it:
 * an Admin Console origin, the Organization section alone. The sidebar decides the frame ONCE —
 * the origin in the page's address where it gives this person a place to stand, else the cold-load
 * rule — so the section can never name a module the sidebar does not (the OM-28 review fold). The
 * Admin Console row is never a module (T4.1).
 */
export function settingsModuleFor(
  frame: string | null,
  moduleSettings: readonly ModuleSettings[],
): OrgSettingsModule | null {
  if (frame === null) return null;
  const row = charityConsoleTable().find(
    (candidate) =>
      candidate.slug === frame &&
      candidate.adminConsole !== true &&
      isMountedInThisBuild(candidate),
  );
  if (row === undefined) return null;
  const declared = moduleSettings.find((declaration) => declaration.slug === frame);
  return { slug: frame, label: row.label, panels: declared?.panels ?? [] };
}

function SettingsBody() {
  const config = useOrgConsoleConfig();
  const { read, standings } = useCharityConsole();
  const { activeCharityId, activeMembership } = useActiveOrg();
  const me = useQuery(usersGetMeOptions());
  // Board's cards need Board's runtime (the app root mounts it); without it they stay out.
  const boardRuntime = useHasBoardRuntime();
  const squares = useConsoleSquares();
  const resolved = useLandingDestination('in-app', { ignoreChosenDefault: true });
  const frame = useConsoleFrame();
  const navigate = useModuleNavigate();
  const { signOut } = useSignOut();
  const declarations = useMemo(() => consoleDeclarations(read.capabilities), [read.capabilities]);
  // CBG-CAL1: "Connected accounts" joins the Organization section only once core lists an account,
  // so while its read is dark (or empty) it brings no empty section with it.
  const showsConnectedAccounts = useConnectedAccountsShown(
    activeCharityId,
    (read.capabilities ?? []).includes(CONNECTED_ACCOUNTS_CARD.capability ?? ''),
  );
  const module = useMemo(
    () => settingsModuleFor(frame, declarations.moduleSettings ?? []),
    [declarations, frame],
  );
  // CBG-CAL1: the "Calendar" card, for whoever schedules meetings — and, wherever Board isn't on for
  // the organization (its strings masked), for anyone, since revoking access never waits on Board:
  // core then lists only the person's own calendar, and the card offers its owner Disconnect alone.
  const boardVerdict = verdictFor(standings, BOARD_SLUG);
  const boardOn = boardVerdict === 'link' || boardVerdict === 'locked';
  // CBG-UX14's Q2: on Board's own door, Board's "Calendar" card holds the calendars the board's
  // meetings go out from, so "Connected accounts" stays out there; on every other door it keeps its
  // place (the settings authority's one list, and revoking never waits on Board).
  const boardCalendarHere =
    module?.slug === BOARD_SLUG &&
    boardOn &&
    (read.permissions ?? []).includes('board_meeting:write');
  const organizationCards = useMemo(
    () =>
      (declarations.organizationSettings ?? []).filter(
        (card) =>
          card.id !== CONNECTED_ACCOUNTS_CARD.id || (showsConnectedAccounts && !boardCalendarHere),
      ),
    [declarations, showsConnectedAccounts, boardCalendarHere],
  );
  // Your settings → Calendar, the person's one calendar connection for every Givmo app (Q3, with
  // D-CBG-55's lines). Left out on Board's door, where Board's "Calendar" card connects,
  // reconnects and disconnects that same connection (D-CBG-56); every other door keeps it.
  const showsYourCalendar =
    isBoardUiEnabled() &&
    boardRuntime &&
    activeCharityId !== null &&
    ((read.permissions ?? []).includes('board_meeting:write') || !boardOn) &&
    !boardCalendarHere;
  // A declared panel's unsaved changes reach the portal's ONE guard: leaving the page prompts.
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty);
  if (config === null) return null;
  const landingOptions = openableSquares(squares).map((square) => ({
    value: square.row.slug,
    label: square.row.label,
  }));
  return (
    <OrgSettingsPanel
      config={config}
      landingOptions={landingOptions}
      defaultResolvesTo={resolved.label}
      module={module}
      standings={standings}
      permissions={read.permissions}
      capabilities={read.capabilities}
      organizationCards={organizationCards}
      yourSettingsLead={
        <>
          <CharitySignInSecurityCard />
          {showsYourCalendar && activeCharityId !== null ? (
            <YourCalendarCard
              orgRef={activeCharityId}
              permissions={read.permissions ?? []}
              userId={me.data?.id ?? null}
              userEmail={me.data?.email ?? null}
              orgName={activeMembership?.name ?? null}
              boardOn={boardOn}
            />
          ) : null}
        </>
      }
      onNavigate={navigate}
      onDirtyChange={setDirty}
      onDeactivated={signOut}
    />
  );
}

export function ConsoleSettingsPage() {
  const { activeCharityId } = useActiveOrg();

  return (
    <PageShell testId="page-settings" title="Settings">
      {activeCharityId === null ? (
        <p className="font-sans text-s13 text-text-tertiary" {...testIdProp('preferences-pending')}>
          Loading…
        </p>
      ) : (
        <SettingsBody />
      )}
    </PageShell>
  );
}
